import { useState } from 'react'
import { ChevronRight, ChevronDown, Folder, FolderOpen, FileCode2, FileJson, FileText, FileType, Image, Database, Settings2, Terminal, Hash, Braces } from 'lucide-react'

const EXT_ICON_MAP = {
  js:     { Icon: Braces,    className: 'file-icon-js' },
  jsx:    { Icon: Braces,    className: 'file-icon-jsx' },
  mjs:    { Icon: Braces,    className: 'file-icon-js' },
  ts:     { Icon: Braces,    className: 'file-icon-ts' },
  tsx:    { Icon: Braces,    className: 'file-icon-tsx' },
  css:    { Icon: Hash,      className: 'file-icon-css' },
  scss:   { Icon: Hash,      className: 'file-icon-scss' },
  html:   { Icon: FileCode2, className: 'file-icon-html' },
  htm:    { Icon: FileCode2, className: 'file-icon-html' },
  json:   { Icon: FileJson,  className: 'file-icon-json' },
  md:     { Icon: FileText,  className: 'file-icon-md' },
  mdx:    { Icon: FileText,  className: 'file-icon-md' },
  py:     { Icon: FileCode2, className: 'file-icon-py' },
  rb:     { Icon: FileCode2, className: 'file-icon-rb' },
  go:     { Icon: FileCode2, className: 'file-icon-go' },
  rs:     { Icon: FileCode2, className: 'file-icon-rs' },
  java:   { Icon: FileCode2, className: 'file-icon-java' },
  kt:     { Icon: FileCode2, className: 'file-icon-kt' },
  cpp:    { Icon: FileCode2, className: 'file-icon-cpp' },
  c:      { Icon: FileCode2, className: 'file-icon-c' },
  swift:  { Icon: FileCode2, className: 'file-icon-swift' },
  dart:   { Icon: FileCode2, className: 'file-icon-dart' },
  php:    { Icon: FileCode2, className: 'file-icon-php' },
  sql:    { Icon: Database,  className: 'file-icon-sql' },
  yml:    { Icon: Settings2, className: 'file-icon-yml' },
  yaml:   { Icon: Settings2, className: 'file-icon-yaml' },
  xml:    { Icon: FileCode2, className: 'file-icon-xml' },
  svg:    { Icon: Image,     className: 'file-icon-svg' },
  sh:     { Icon: Terminal,  className: 'file-icon-sh' },
  bash:   { Icon: Terminal,  className: 'file-icon-sh' },
  env:    { Icon: Settings2, className: 'file-icon-env' },
  png:    { Icon: Image,     className: 'file-icon-default' },
  jpg:    { Icon: Image,     className: 'file-icon-default' },
  gif:    { Icon: Image,     className: 'file-icon-default' },
  toml:   { Icon: Settings2, className: 'file-icon-default' },
}

function getFileIconInfo(fileName) {
  if (!fileName) return { Icon: FileType, className: 'file-icon-default' }
  const lower = fileName.toLowerCase()
  if (lower === 'dockerfile') return { Icon: FileCode2, className: 'file-icon-docker' }
  if (lower === '.gitignore') return { Icon: FileCode2, className: 'file-icon-git' }
  if (lower === 'package.json') return { Icon: FileJson, className: 'file-icon-json' }
  const ext = lower.includes('.') ? lower.split('.').pop() : ''
  return EXT_ICON_MAP[ext] || { Icon: FileType, className: 'file-icon-default' }
}

function TreeNode({ node, onOpenFile, depth = 0, activeFilePath }) {
  const [isOpen, setIsOpen] = useState(false)

  if (node.type === 'directory') {
    const FolderIcon = isOpen ? FolderOpen : Folder
    const folderClass = isOpen ? 'file-icon-folder-open' : 'file-icon-folder'

    return (
      <div>
        <div
          className="tree-directory"
          style={{ paddingLeft: `${8 + depth * 16}px` }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>
            <ChevronRight
              size={12}
              strokeWidth={2}
              style={{
                transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 150ms ease',
                flexShrink: 0,
              }}
            />
            <FolderIcon size={15} className={folderClass} strokeWidth={1.8} />
            {node.name}
          </span>
        </div>
        {isOpen && node.children?.map(child => (
          <TreeNode
            key={child.path}
            node={child}
            onOpenFile={onOpenFile}
            depth={depth + 1}
            activeFilePath={activeFilePath}
          />
        ))}
      </div>
    )
  }

  const { Icon, className } = getFileIconInfo(node.name)
  const isActive = node.path === activeFilePath

  return (
    <div
      className={`tree-file ${isActive ? 'active' : ''}`}
      style={{ paddingLeft: `${24 + depth * 16}px` }}
      onClick={() => onOpenFile(node)}
    >
      <span>
        <Icon size={14} className={className} strokeWidth={1.8} />
        {node.name}
      </span>
    </div>
  )
}

export function FileExplorer({ project, onOpenFile, activeFilePath }) {
  if (!project) {
    return (
      <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, padding: 24 }}>
        <Folder size={32} style={{ color: 'var(--text-disabled)', opacity: 0.5 }} strokeWidth={1.2} />
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', textAlign: 'center', lineHeight: 1.5 }}>
          Open a project folder<br />to start editing
        </span>
      </div>
    )
  }

  return (
    <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="tree-container" style={{ flex: 1, overflowY: 'auto' }}>
        {project.tree.map(node => (
          <TreeNode
            key={node.path}
            node={node}
            onOpenFile={onOpenFile}
            activeFilePath={activeFilePath}
          />
        ))}
      </div>
    </div>
  )
}
